import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MediaFileInput, TagTask, TagTaskInput} from '../../../../../generated/graphql';
import {Store} from '@ngrx/store';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {TagsService} from '../../tags.service';
import {map} from 'rxjs/operators';
import {Gallery, ImageItem} from '@ngx-gallery/core';
import {MediaFileStateEnum} from '../../../../shared/enums/media-file-state-enum';
import {GlobalService} from '../../../../shared/services/global.service';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin} from 'rxjs';
import {CustomPermissionsService} from '../../../../shared/services/custom-permissions.service';
import {EnumsHelper} from '../../../../shared/helpers/enums-helper.service';
import {MediaService} from '../../../../shared/services/media.service';
import {TagTypes} from '../../../../shared/enums/entity-enums';
import {AppState} from '../../../../store/app.reducer';
import {SetBreadcrumb, SetToolbarButtons} from '../../../../fixed-toolbar/fixed-toolbar.action';
import {TagTaskBrowseFetch, TagTaskFetch, TagTaskSet, TagTaskUpdate} from '../store/tag-tasks.actions';
import {currentStateGetCurrentProject} from '../../../../store/current-state/current-state.selector';
import {TagBaseComponent} from '../../tag-base/tag-base.component';

@Component({
  selector: 'app-tag-task-edit',
  templateUrl: './tag-task-edit.component.html',
  styleUrls: ['./tag-task-edit.component.css']
})
export class TagTaskEditComponent extends TagBaseComponent implements OnInit, OnDestroy, AfterViewInit {

    tagType = TagTypes.TAG_TASK;

    loadedTagTask: TagTask;

    tagAction = 'update';

    constructor(
        protected store: Store<AppState>,
        protected gs: GlobalService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected tagsService: TagsService,
        protected gallery: Gallery,
        private media: MediaService,
        private translate: TranslateService,
        protected ps: CustomPermissionsService,
        protected enums: EnumsHelper,
    ) {
        super(store, gs, tagsService, ps, enums, gallery);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.subs.sink = this.route.params.subscribe((params: Params) => {

            const browseFullId = params.fullId;
            const browseProjectKeyName = params.projectKeyName;
            const browseTagSequenceNumber = +params.tagSequenceNumber;
            let browseEditMode = false;

            const editId = +params.id;
            const editMode = params.id != null;

            if (!editMode && browseFullId) {
                browseEditMode = true;
            }

            forkJoin([
                this.translate.get('tasks.title_m'),
                this.translate.get('tasks.edit'),
                this.translate.get('tasks.create'),
            ]).subscribe(result => {
                const breadCrumbItems = [];
                breadCrumbItems.push({label: result[0], routerLink: ['/tasks']});

                if (editMode) {
                    breadCrumbItems.push({label: result[1], routerLink: ['/tasks/edit', editId]});
                    this.fetchTask(editId);
                } else if (browseEditMode) {
                    breadCrumbItems.push({label: browseProjectKeyName});
                    breadCrumbItems.push({label: browseFullId, routerLink: ['/browse/', browseFullId]});
                    this.fetchTaskBrowse(browseProjectKeyName, browseTagSequenceNumber);
                } else {
                    breadCrumbItems.push({label: result[2], routerLink: '/tasks/create'});
                }
                this.store.dispatch(new SetBreadcrumb(breadCrumbItems));

                this.galleryImages1 = [];
                this.galleryImages2 = [];

                this.uploadManager1.clearUploadedFiles(null);
                this.uploadManager2.clearUploadedFiles(null);

                this.image1Upload?.clear();
                this.image2Upload?.clear();
            });
        });

        this.subs.sink = this.ps.getNgxPermissions$().subscribe(() => {
            this.subs.sink = this.store.select(currentStateGetCurrentProject).subscribe((project) => {
                this.subscribeTagStates(project, this.loadedTagTask);

                forkJoin({
                    getPerm: this.ps.hasProjectPermission(project, 'getTasks'),
                    resolvePerm: this.ps.hasProjectPermission(project, 'resolveTasks'),
                    updatePerm: this.ps.hasProjectPermission(project, 'updateTasks'),
                    createPerm: this.ps.hasProjectPermission(project, 'createTasks')
                }).subscribe(results => {
                    this.buttons = [...this.buttons];

                    if (results.getPerm) {
                        this.permission = 'get';
                    }

                    if (results.resolvePerm) {
                        this.permission = 'resolve';
                        this.tagAction = 'resolve';
                    }

                    if (results.updatePerm) {
                        this.tagAction = 'update';
                        this.permission = 'update';

                        this.buttons.push(
                            {
                                id: 'saveTaskButton',
                                name: 'tasks.save',
                                action: 'onSaveTask',
                                class: 'p-button-success',
                                icon: 'pi pi-save p-mr-1',
                                disabled: false,
                            }
                        );
                    }

                    if (results.createPerm) {
                        this.buttons.push(
                            {
                                id: 'createTaskButton',
                                name: 'tasks.create',
                                action: 'onCreateTask',
                                class: 'p-button-primary',
                                icon: 'pi pi-plus',
                                disabled: false,
                            }
                        );

                    }
                    this.store.dispatch(new SetToolbarButtons(this.buttons));
                });
            });
        });
    }

    ngAfterViewInit() {
        // Load items into gallery
        const gallery1Ref = this.gallery.ref(this.images1Id, {
            thumbPosition: 'top',
            imageSize: 'contain'
        });
        // Load items into gallery
        const gallery2Ref = this.gallery.ref(this.images2Id, {
            thumbPosition: 'top',
            imageSize: 'contain'
        });

        this.subs.sink = this.store.select('tagTasks').pipe(
            map(subjectState => {
                return {
                    loadedTagTask: subjectState.loadedTagTask,
                };
            })).subscribe(
            ({
                 loadedTagTask: loadedTagTask,
             }) => {
                if (loadedTagTask) {
                    this.tagForm.form.patchValue({
                            ...loadedTagTask,
                            taskId: loadedTagTask.id,
                            assignedToUser: loadedTagTask.assignedToUser,
                            deadline: loadedTagTask.deadline ? new Date(loadedTagTask.deadline.date) : null,
                            occurredAt: loadedTagTask.occurredAt ? new Date(loadedTagTask.occurredAt.date) : null,
                        }
                    );
                    this.store.dispatch(new TagTaskSet({task: null}));

                    this.loadedTagTask = loadedTagTask;
                    this.selectedProject = this.loadedTagTask.project;

                    this.loadedImages1 = [...loadedTagTask.images];
                    this.loadedImages2 = [...loadedTagTask.resolvedImages];

                    this.loadedImages1.forEach((fileCode) => {
                        if (!this.galleryImages1.find(item => item.data.code === fileCode)) {
                            this.galleryImages1.push(new ImageItem({
                                src: this.media.getMediaLink(fileCode),
                                thumb: this.media.getMediaThumbLink(fileCode),
                                code: fileCode
                            }));
                        }
                    });

                    this.loadedImages2.forEach((fileCode) => {
                        if (!this.galleryImages2.find(item => item.data.code === fileCode)) {
                            this.galleryImages2.push(new ImageItem({
                                src: this.media.getMediaLink(fileCode),
                                thumb: this.media.getMediaThumbLink(fileCode),
                                code: fileCode
                            }));
                        }
                    });

                    gallery1Ref.load(this.galleryImages1);
                    gallery2Ref.load(this.galleryImages2);

                    this.uploadManager1.clearUploadedFilesList();
                    this.uploadManager2.clearUploadedFilesList();

                    this.image1Upload?.clear();
                    this.image2Upload?.clear();

                    this.isLoading = false;
                }
            });
    }

    fetchTask(taskId: number) {
        this.store.dispatch(new TagTaskFetch({taskId}));
    }

    fetchTaskBrowse(projectKeyName: string, tagSequenceNumber: number) {
        this.store.dispatch(new TagTaskBrowseFetch({projectKeyName, tagSequenceNumber}));
    }

    onSaveTask() {
        this.tagForm.ngSubmit.emit();
    }

    onSubmit(form: NgForm) {
        if (this.tagAction === 'update') {
            const task: TagTaskInput = {
                id: (form.value.taskId !== null) ? +form.value.taskId : null,
                state: form.value.state.id,
                severity: form.value.severity.id,
                project: this.loadedTagTask.project.id,
                assignedToSubjects: form.value.assignedToSubjects?.map(s => s.id) ?? [],
                assignedToSubjectNote: form.value?.assignedToSubjectNote ?? null,
                assignedToUser: form.value.assignedToUser?.id ?? null,
                areaOf: form.value.areaOf?.id ?? null,
                occurredAt: form.value.occurredAt,
                deadline: form.value.deadline,
                deadlineTerm: form.value.deadlineTerm,
                description: form.value.description,
                resolvedDescription: form.value.resolvedDescription,
                images: [...( this.galleryImages1.map((file) => {
                    const mediaFile: MediaFileInput = {
                        fileName: file.data.code,
                        fileState: MediaFileStateEnum.STATE_EXISTING
                    };
                    return mediaFile;
                }) ?? []), ...this.uploadManager1.getFilesNameArray()],
                resolvedImages: [...( this.galleryImages2.map((file) => {
                    const mediaFile: MediaFileInput = {
                        fileName: file.data.code,
                        fileState: MediaFileStateEnum.STATE_EXISTING
                    };
                    return mediaFile;
                }) ?? []), ...this.uploadManager2.getFilesNameArray()],
                note: form.value.note,
                location: form.value.location,
            };
            this.store.dispatch(new TagTaskUpdate({task, action: this.tagAction}));
        } else {
            const task: TagTaskInput = {
                id: (form.value.taskId !== null) ? +form.value.taskId : null,
                state: form.value.state.id,
                severity: 0,
                project: 0,
                assignedToSubjects: [],
                assignedToSubjectNote: null,
                assignedToUser: null,
                areaOf: 0,
                occurredAt: null,
                deadline: null,
                deadlineTerm: null,
                description: null,
                resolvedDescription: form.value.resolvedDescription,
                images: [],
                resolvedImages: [...( this.galleryImages2.map((file) => {
                    const mediaFile: MediaFileInput = {
                        fileName: file.data.code,
                        fileState: MediaFileStateEnum.STATE_EXISTING
                    };
                    return mediaFile;
                }) ?? []), ...this.uploadManager2.getFilesNameArray()]
            };
            this.store.dispatch(new TagTaskUpdate({task, action: this.tagAction}));
        }
    }
}
